import { useState } from "react";
import { Icons, PRODUCT_ICON_MAP, PRODUCT_COLOR_MAP } from "./bookingConstants";
import f from "./ProductCard.module.css";

const STATUS_COLORS = {
  Pending:   { color: "#d97706", bg: "#fef3c7" },
  Option:    { color: "#6366f1", bg: "#eef2ff" },
  Confirmed: { color: "#059669", bg: "#ecfdf5" },
  Cancelled: { color: "#dc2626", bg: "#fef2f2" },
};

const fmtDate = (dateStr) => {
  if (!dateStr) return "";
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateStr)) return dateStr;
  const parts = dateStr.split("-");
  if (parts.length === 3) return `${parts[2]}.${parts[1]}.${parts[0]}`;
  return dateStr;
};

const fmtTime = (timeStr) => {
  if (!timeStr) return "";
  return timeStr.slice(0, 5);
};

/* ─── Inline Icons ───────────────────────────────────────────────────── */

const Star = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="#f59e0b" stroke="#f59e0b" strokeWidth="1">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const WarningIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
    <path d="M12 9v4" />
    <path d="M12 17h.01" />
  </svg>
);

/* ─── Flight Card ───────────────────────────────────────────────────── */

/* ─── Shared Info Bar Grid ─────────────────────────────────────────── */

const InfoBar = ({ items }) => (
  <div style={{ display: "grid", gridTemplateColumns: `repeat(${items.length}, 1fr)`, background: "#fff", borderBottom: "1px solid #e5e7eb" }}>
    {items.map((item, idx) => (
      <div key={idx} style={{ padding: "10px 16px", borderRight: idx < items.length - 1 ? "1px solid #f0f0f0" : "none" }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>{item.label}</div>
        <div style={{
          fontSize: 13, fontWeight: 600,
          color: item.highlight ? "#2563eb" : item.status
            ? (item.value === "Confirmed" ? "#059669" : item.value === "Cancelled" ? "#dc2626" : "#d97706")
            : "#111827",
        }}>{item.value || "—"}</div>
      </div>
    ))}
  </div>
);

const CardHeader = ({ icon, iconClass, label, status, onEdit, onRemove, product }) => {
  const sc = STATUS_COLORS[status] || STATUS_COLORS.Pending;
  return (
    <div className={f.header} style={{ padding: "10px 16px" }}>
      <div className={`${f.headerIcon} ${iconClass}`}>{icon}</div>
      <span style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>{label}</span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11.5, fontWeight: 600, color: sc.color }}>
        <span style={{ width: 7, height: 7, borderRadius: "50%", background: sc.color }} />
        {status || "Pending"}
      </span>
      <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
        <button className={f.actionBtn} onClick={() => onEdit(product)}>{Icons.edit} Edit</button>
        <button className={`${f.actionBtn} ${f.actionBtnDanger}`} onClick={() => onRemove(product.id)}>{Icons.trash} Delete</button>
      </div>
    </div>
  );
};

const FlightCard = ({ product, onEdit, onRemove }) => {
  const legs        = product.legs || [];
  const outboundLegs = legs.filter((l) => l.direction === "Outbound");
  const inboundLegs  = legs.filter((l) => l.direction === "Inbound");
  const isSplit      = product.flightPricingMode === "SPLIT";
  const pnr          = product.airlinePnr || (isSplit ? product.outboundPnr : "") || "";
  const ftMap        = { OW: "One Way", RT: "Round Trip", Multi: "Multi-City" };
  const flightType   = ftMap[product.flightType] || product.flightType || "";
  const vatLabel     = product.supplierCommercialRole === "AGENT" ? "Agent" : product.supplierCommercialRole === "MERCHANT" ? "Merchant" : product.supplierCommercialRole || "—";

  return (
    <div className={f.card}>
      <CardHeader icon={Icons.plane} iconClass={f.headerIconFlight} label="FLIGHT" status={product.status} onEdit={onEdit} onRemove={onRemove} product={product} />

      <InfoBar items={[
        { label: "Supplier", value: product.supplier || "—" },
        { label: "Supplier Ref", value: product.supplierRef || "—" },
        { label: "Type", value: flightType || "—", highlight: true },
        { label: "Pricing", value: isSplit ? "Split Flight" : "Full Flight" },
        { label: "VAT", value: vatLabel },
      ]} />

      {/* Flight segments grid */}
      {legs.length > 0 && (() => {
        const colStyle = "90px 130px 1fr 100px 1fr";
        const headStyle = { fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", padding: "7px 0" };
        return (
          <div style={{ background: "#f8fafc", borderBottom: "1px solid #e5e7eb" }}>
            <div style={{ display: "grid", gridTemplateColumns: colStyle, padding: "0 16px", borderBottom: "1px solid #eee" }}>
              <div style={headStyle} />
              <div style={headStyle}>Route</div>
              <div style={headStyle}>Departure</div>
              <div style={headStyle}>Flight No</div>
              <div style={headStyle}>PNR</div>
            </div>
            {legs.map((leg, li) => {
              const depCode = (leg.departure || "").toUpperCase();
              const arrCode = (leg.arrival || "").toUpperCase();
              const isOut = leg.direction !== "Inbound";
              const flightCode = [leg.airlineCode, leg.flightNumber].filter(Boolean).join("");
              return (
                <div key={li} style={{ display: "grid", gridTemplateColumns: colStyle, fontSize: 12, color: "#64748b", padding: "0 16px", borderBottom: li < legs.length - 1 ? "1px solid #f0f0f0" : "none" }}>
                  <div style={{ padding: "9px 0" }}>
                    <span style={{
                      padding: "2px 8px", borderRadius: 4, fontSize: 10, fontWeight: 700, letterSpacing: "0.04em",
                      background: isOut ? "#eef9f3" : "#eef3ff", color: isOut ? "#0d7a4f" : "#3b5bdb",
                      border: `1px solid ${isOut ? "#c3e8d4" : "#d0dafb"}`
                    }}>{(leg.direction || "OUTBOUND").toUpperCase()}</span>
                  </div>
                  <div style={{ padding: "9px 0", fontWeight: 700, color: "#1a1a2e", fontSize: 13 }}>{depCode} → {arrCode}</div>
                  <div style={{ padding: "9px 0" }}>{fmtDate(leg.date)} {fmtTime(leg.time)}</div>
                  <div style={{ padding: "9px 0", fontFamily: "monospace", fontWeight: 600 }}>{flightCode || "—"}</div>
                  <div style={{ padding: "9px 0", fontWeight: 600, color: "#7c3aed" }}>{pnr ? `PNR: ${pnr.toUpperCase()}` : ""}</div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {legs.length === 0 && <div className={f.emptyLegs}>No legs added yet</div>}
    </div>
  );
};

const groupRooms = (rooms = []) => {
  const map = new Map();
  rooms.forEach((r) => {
    const key = [
      r.roomType || "",
      r.boardName || r.boardCode || "",
      r.roomName || "",
      r.roomCharacteristic || "",
    ].join("|");
    const qty = r.numberOfRooms || 1;
    if (map.has(key)) {
      map.get(key).qty += qty;
    } else {
      map.set(key, { ...r, qty });
    }
  });
  return Array.from(map.values());
};

const HotelCard = ({ product, onEdit, onRemove }) => {
  let city = product.hotelCity || "";
  let country = product.hotelCountry || "";
  if ((!city || !country) && product.hotelAddress) {
    const parts = product.hotelAddress.split(/[,/]/).map((p) => p.trim()).filter(Boolean);
    if (!city && parts.length > 0) city = parts[parts.length - 2] || parts[0];
    if (!country && parts.length > 1) country = parts[parts.length - 1];
  }
  const destination = [city, country].filter(Boolean).join(", ");
  const stars = Number.isFinite(product.hotelStars) ? product.hotelStars : 0;

  let rooms = [];
  if (Array.isArray(product.rooms) && product.rooms.length > 0) {
    rooms = groupRooms(product.rooms);
  } else if (product.roomType || product.boardName || product.roomName) {
    rooms = [{ qty: product.numberOfRooms || 1, roomType: product.roomType, roomName: product.roomName, boardName: product.boardName, boardCode: product.boardCode, roomCharacteristic: product.roomCharacteristic }];
  }
  const totalRooms = rooms.reduce((s, r) => s + r.qty, 0);
  const boardVal = product.boardName || product.boardCode || rooms[0]?.boardName || rooms[0]?.boardCode || "—";
  const roomType = rooms[0] ? [rooms[0].roomType, rooms[0].roomName].filter(Boolean).join(" ") || "Room" : "—";
  const roomChar = rooms[0]?.roomCharacteristic || "—";
  const vatLabel = product.supplierCommercialRole === "AGENT" ? "Agent" : product.supplierCommercialRole === "MERCHANT" ? "Merchant" : product.supplierCommercialRole || "—";

  return (
    <div className={f.card}>
      <CardHeader icon={Icons.hotel} iconClass={f.headerIconHotel} label="HOTEL" status={product.status} onEdit={onEdit} onRemove={onRemove} product={product} />

      <InfoBar items={[
        { label: "Supplier Name", value: product.supplier || "—" },
        { label: "Supplier Ref", value: product.supplierRef || "—" },
        { label: "Travelers", value: `${totalRooms} room${totalRooms !== 1 ? "s" : ""}` },
        { label: "Status", value: product.status || "Pending", status: true },
        { label: "VAT Role", value: vatLabel },
      ]} />

      {/* Hotel name + location */}
      <div style={{ background: "#f8fafc", borderBottom: "1px solid #e5e7eb" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 24, padding: "12px 20px", borderBottom: "1px solid #eef2f7" }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>Hotel Name</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#111827", display: "flex", alignItems: "center", gap: 6 }}>
              {product.hotelName || "—"}
              {stars > 0 && <span style={{ display: "inline-flex", gap: 1 }}>{Array.from({ length: stars }).map((_, i) => <Star key={i} />)}</span>}
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>City / Country</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#374151" }}>{destination || product.hotelAddress || "—"}</div>
          </div>
        </div>
        {/* Detail grid */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
          {[
            { label: "Check-in", value: fmtDate(product.checkIn) || "—" },
            { label: "Check-out", value: fmtDate(product.checkOut) || "—" },
            { label: "Nights", value: product.nights ? `${product.nights} nights` : "—" },
            { label: "Rooms", value: `${totalRooms} room${totalRooms !== 1 ? "s" : ""}` },
            { label: "Board", value: boardVal },
            { label: "Room Type", value: roomType },
            { label: "Room Char.", value: roomChar },
          ].map((item, idx) => (
            <div key={idx} style={{ padding: "10px 16px", borderRight: idx < 6 ? "1px solid #eef2f7" : "none" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>{item.label}</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{item.value}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ─── Transfer Card ────────────────────────────────────────────────── */

const TransferCard = ({ product, onEdit, onRemove }) => {
  const moment       = product.transferMoment || "";
  const isRoundTrip  = moment === "RoundTrip";
  const transferType = product.transferType || "";
  const vehicleType  = product.vehicleType || "";
  const vatLabel     = product.supplierCommercialRole === "AGENT" ? "Agent" : product.supplierCommercialRole === "MERCHANT" ? "Merchant" : product.supplierCommercialRole || "—";
  const momentLabel  = moment ? `${moment.toUpperCase()} TRANSFER` : "TRANSFER";

  // A leg may be flat (UI form) or nested under .schedule (saved booking). Support both.
  const legField = (leg, key) => (leg?.[key] ?? leg?.schedule?.[key] ?? "");

  // Render a 5-column detail grid for a set of fields.
  const detailGrid = (rows) => (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)" }}>
      {rows.map((item, idx) => (
        <div key={idx} style={{ padding: "10px 16px", borderRight: idx < rows.length - 1 ? "1px solid #eef2f7" : "none" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>{item.label}</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{item.value}</div>
        </div>
      ))}
    </div>
  );

  const legRows = (leg) => [
    { label: "Pickup Location", value: (legField(leg, "pickupLocationLabel") || legField(leg, "pickupLocation")) || "—" },
    { label: "Dropoff Location", value: (legField(leg, "dropoffLocationLabel") || legField(leg, "dropoffLocation")) || "—" },
    { label: "Pickup Date", value: fmtDate(legField(leg, "pickupDate")) || "—" },
    { label: "Pickup Time", value: fmtTime(legField(leg, "pickupTime")) || "—" },
    { label: "Vehicle Type", value: vehicleType || "—" },
  ];

  return (
    <div className={f.card}>
      <CardHeader icon={Icons.bus} iconClass={f.headerIconTransfer} label={momentLabel} status={product.status} onEdit={onEdit} onRemove={onRemove} product={product} />

      <InfoBar items={[
        { label: "Supplier Name", value: product.supplier || "—" },
        { label: "Supplier Ref", value: product.supplierRef || "—" },
        { label: "Transfer Type", value: transferType || "—" },
        { label: "Status", value: product.status || "Pending", status: true },
        { label: "VAT Role", value: vatLabel },
      ]} />

      {/* Transfer Details — Round Trip shows both legs; one-way shows a single schedule */}
      <div style={{ background: "#f8fafc", borderBottom: "1px solid #e5e7eb" }}>
        {isRoundTrip ? (
          [
            { title: "Arrival", color: "#0d9488", leg: product.arrivalLeg || {} },
            { title: "Departure", color: "#2563eb", leg: product.departureLeg || {} },
          ].map((blk, bi) => (
            <div key={blk.title} style={{ borderTop: bi > 0 ? "1px solid #e5e7eb" : "none" }}>
              <div style={{ padding: "8px 16px 2px", fontSize: 9.5, fontWeight: 800, color: blk.color, textTransform: "uppercase", letterSpacing: "0.06em" }}>{blk.title} Transfer</div>
              {detailGrid(legRows(blk.leg))}
            </div>
          ))
        ) : (
          detailGrid([
            { label: "Pickup Location", value: (product.pickupLocationLabel || product.pickupLocation) || "—" },
            { label: "Dropoff Location", value: (product.dropoffLocationLabel || product.dropoffLocation) || "—" },
            { label: "Pickup Date", value: fmtDate(product.pickupDate) || "—" },
            { label: "Pickup Time", value: fmtTime(product.pickupTime) || "—" },
            { label: "Vehicle Type", value: vehicleType || "—" },
          ])
        )}
      </div>
    </div>
  );
};

/* ─── Generic Card (for non-Flight, non-Hotel, non-Transfer) ─────── */

const GENERIC_ICON_CLASS = {
  Insurance: f.headerIconInsurance,
  CarRental: f.headerIconCarRental,
  Visa: f.headerIconVisa,
  Cruise: f.headerIconCruise,
  Package: f.headerIconPackage,
};

const getGenericDetailItems = (product) => {
  const vatLabel = product.supplierCommercialRole === "AGENT" ? "Agent" : product.supplierCommercialRole === "MERCHANT" ? "Merchant" : product.supplierCommercialRole || "—";
  switch (product.type) {
    case "Insurance":
      return [
        { label: "Insurance Type", value: product.insuranceType || "—" },
        { label: "Coverage Area", value: product.coverageArea || "—" },
        { label: "Start Date", value: fmtDate(product.startDate) || "—" },
        { label: "End Date", value: fmtDate(product.endDate) || "—" },
      ];
    case "CarRental":
      return [
        { label: "Car Category", value: product.carCategory || "—" },
        { label: "Pickup Date", value: fmtDate(product.pickupDate) || "—" },
        { label: "Dropoff Date", value: fmtDate(product.dropoffDate) || "—" },
        { label: "Pickup Location", value: product.pickupLocation || "—" },
      ];
    case "Visa":
      return [
        { label: "Visa Type", value: product.visaType || "—" },
        { label: "Destination", value: product.destination || "—" },
        { label: "Application Date", value: fmtDate(product.applicationDate) || "—" },
        { label: "VAT Role", value: vatLabel },
      ];
    case "Cruise":
      return [
        { label: "Cruise Line", value: product.cruiseLine || "—" },
        { label: "Embark Date", value: fmtDate(product.embarkDate) || "—" },
        { label: "Disembark Date", value: fmtDate(product.disembarkDate) || "—" },
        { label: "Cabin Type", value: product.cabinType || "—" },
      ];
    case "Package":
      return [
        { label: "Package Name", value: product.packageName || "—" },
        { label: "Start Date", value: fmtDate(product.startDate) || "—" },
        { label: "End Date", value: fmtDate(product.endDate) || "—" },
        { label: "Destination", value: product.destination || "—" },
      ];
    default:
      return [
        { label: "VAT Role", value: vatLabel },
      ];
  }
};

const GenericCard = ({ product, onEdit, onRemove }) => {
  const icon = PRODUCT_ICON_MAP[product.type] || Icons.box;
  const iconClass = GENERIC_ICON_CLASS[product.type] || f.headerIconGeneric;
  const vatLabel = product.supplierCommercialRole === "AGENT" ? "Agent" : product.supplierCommercialRole === "MERCHANT" ? "Merchant" : product.supplierCommercialRole || "—";
  const detailItems = getGenericDetailItems(product);

  return (
    <div className={f.card}>
      <CardHeader icon={icon} iconClass={iconClass} label={product.type?.toUpperCase() || "PRODUCT"} status={product.status} onEdit={onEdit} onRemove={onRemove} product={product} />

      <InfoBar items={[
        { label: "Supplier Name", value: product.supplier || "—" },
        { label: "Supplier Ref", value: product.supplierRef || "—" },
        { label: "Status", value: product.status || "Pending", status: true },
        { label: "VAT Role", value: vatLabel },
      ]} />

      {detailItems.length > 0 && (
        <div style={{ background: "#f8fafc", borderBottom: "1px solid #e5e7eb" }}>
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${detailItems.length}, 1fr)` }}>
            {detailItems.map((item, idx) => (
              <div key={idx} style={{ padding: "10px 16px", borderRight: idx < detailItems.length - 1 ? "1px solid #eef2f7" : "none" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>{item.label}</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{item.value}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Confirm Remove Modal ─────────────────────────────────────────── */

const ConfirmRemoveModal = ({ product, onCancel, onConfirm }) => {
  /* Derive a human-readable label for the product */
  const getProductLabel = () => {
    if (product.type === "Flight") {
      const firstLeg = (product.legs || [])[0] || {};
      const airline = firstLeg.airlineName || product.supplier || "Flight";
      return firstLeg.departure && firstLeg.arrival
        ? `${airline} · ${firstLeg.departure} → ${firstLeg.arrival}`
        : airline;
    }
    if (product.type === "Hotel") {
      return product.hotelName || "Hotel";
    }
    if (product.type === "Transfer") {
      const moment = product.transferMoment ? `${product.transferMoment} ` : "";
      const route = [product.pickupLocation, product.dropoffLocation].filter(Boolean).join(" → ");
      return route ? `${moment}Transfer · ${route}` : `${moment}Transfer`;
    }
    return product.supplier || product.type;
  };

  return (
    <div
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.45)",
        backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 200,
        padding: 20,
        animation: "fadeIn 0.15s ease",
      }}
      onClick={onCancel}
    >
      <div
        style={{
          background: "var(--surface)",
          borderRadius: "var(--radius-xl)",
          width: 440, maxWidth: "92vw",
          boxShadow: "var(--shadow-lg)",
          overflow: "hidden",
          animation: "slideUp 0.18s ease",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: "22px 24px 12px", display: "flex", alignItems: "flex-start", gap: 14 }}>
          <div style={{
            width: 42, height: 42, borderRadius: 12,
            background: "#fef2f2", color: "#dc2626",
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>
            <WarningIcon />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 style={{
              fontSize: 15.5, fontWeight: 700, color: "var(--text-primary)",
              margin: 0, marginBottom: 4, letterSpacing: "-0.01em",
            }}>
              Remove this product?
            </h3>
            <p style={{ fontSize: 13, color: "var(--text-tertiary)", margin: 0, lineHeight: 1.5 }}>
              This will permanently remove the {product.type?.toLowerCase() || "product"} from the booking. This action cannot be undone.
            </p>
          </div>
        </div>

        {/* Product preview strip */}
        <div style={{
          margin: "0 24px 16px",
          padding: "10px 12px",
          background: "var(--surface-raised)",
          border: "1px solid var(--border-light)",
          borderRadius: "var(--radius-sm)",
          display: "flex", alignItems: "center", gap: 10,
        }}>
          <div style={{
            width: 28, height: 28, borderRadius: "var(--radius-sm)",
            background: (PRODUCT_COLOR_MAP[product.type] || {}).bg || "var(--surface-sunken)",
            color: (PRODUCT_COLOR_MAP[product.type] || {}).color || "var(--text-tertiary)",
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>
            {PRODUCT_ICON_MAP[product.type] || Icons.box}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 10, fontWeight: 700, color: "var(--text-quaternary)",
              textTransform: "uppercase", letterSpacing: "0.05em",
            }}>
              {product.type}
            </div>
            <div style={{
              fontSize: 12.5, fontWeight: 600, color: "var(--text-primary)",
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              marginTop: 1,
            }}>
              {getProductLabel()}
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div style={{
          padding: "12px 24px 18px",
          display: "flex", justifyContent: "flex-end", gap: 8,
        }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "9px 18px",
              borderRadius: "var(--radius-sm)",
              border: "1.5px solid var(--border)",
              background: "var(--surface)",
              color: "var(--text-secondary)",
              fontSize: 13, fontWeight: 600, cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "9px 20px",
              borderRadius: "var(--radius-sm)",
              border: "none",
              background: "linear-gradient(135deg, #dc2626, #b91c1c)",
              color: "white",
              fontSize: 13, fontWeight: 700, cursor: "pointer",
              boxShadow: "0 1px 3px rgba(220, 38, 38, 0.3)",
              fontFamily: "inherit",
            }}
          >
            {Icons.trash} Remove
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Router ─────────────────────────────────────────────────────────── */

const ProductCard = ({ product, onEdit, onRemove }) => {
  const [showConfirm, setShowConfirm] = useState(false);

  const requestRemove = () => setShowConfirm(true);
  const confirmRemove = () => {
    setShowConfirm(false);
    onRemove(product.id);
  };

  let card;
  if (product.type === "Flight") {
    card = <FlightCard product={product} onEdit={onEdit} onRemove={requestRemove} />;
  } else if (product.type === "Hotel") {
    card = <HotelCard product={product} onEdit={onEdit} onRemove={requestRemove} />;
  } else if (product.type === "Transfer") {
    card = <TransferCard product={product} onEdit={onEdit} onRemove={requestRemove} />;
  } else {
    card = <GenericCard product={product} onEdit={onEdit} onRemove={requestRemove} />;
  }

  return (
    <>
      {card}
      {showConfirm && (
        <ConfirmRemoveModal
          product={product}
          onCancel={() => setShowConfirm(false)}
          onConfirm={confirmRemove}
        />
      )}
    </>
  );
};

export default ProductCard;